import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import type { DocAIExtraction } from '../../api/docai'
import { PhaseIndicator } from './PhaseIndicator'
import { PhaseMessages } from './PhaseMessages'
import { ProgressTrack } from './ProgressTrack'

interface ProcessingCardProps {
  filename: string
  /** The in-flight extraction request. The card paces its phase animation as
   *  a minimum and stretches the last phase until this settles. */
  task: Promise<DocAIExtraction>
  onComplete: (result: DocAIExtraction) => void
  /** Called when the task rejects; the parent renders the error state. */
  onError: (err: unknown) => void
}

// Minimum per-phase pacing (ms). Real extractions take ~5-15s; the animation
// never finishes faster than this sum so the phases stay readable, and the
// last phase holds until the request settles.
const PHASE_MIN_DURATIONS = [900, 900, 1100, 1200, 900, 700]
const TOTAL_PHASES = PHASE_MIN_DURATIONS.length

// The timed phases advance the bar to this point; the remainder creeps while
// waiting on the network and snaps to 100 on completion.
const PHASE_PROGRESS_CEILING = 90
const CREEP_CEILING = 97

export function ProcessingCard({ filename, task, onComplete, onError }: ProcessingCardProps) {
  const reducedMotion = useReducedMotion()
  const [currentPhase, setCurrentPhase] = useState(0)
  const [completedPhases, setCompletedPhases] = useState<number[]>([])
  const [progress, setProgress] = useState(0)
  const [isDone, setIsDone] = useState(false)
  const [fieldCount, setFieldCount] = useState(0)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    let cancelled = false
    let settled = false
    let result: DocAIExtraction | null = null
    let error: unknown = null
    let failed = false
    task.then(
      (r) => { settled = true; result = r },
      (e) => { settled = true; failed = true; error = e },
    )

    const frame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))

    async function runPhases() {
      for (let i = 0; i < TOTAL_PHASES; i++) {
        if (cancelled) return
        // A failed request cuts the animation short; success keeps pacing.
        if (failed) break
        setCurrentPhase(i)
        const startProgress = Math.round((i / TOTAL_PHASES) * PHASE_PROGRESS_CEILING)
        const targetProgress = Math.round(((i + 1) / TOTAL_PHASES) * PHASE_PROGRESS_CEILING)
        const duration = PHASE_MIN_DURATIONS[i]
        const start = performance.now()

        while (!cancelled && !failed) {
          const pct = Math.min((performance.now() - start) / duration, 1)
          setProgress(Math.round(startProgress + (targetProgress - startProgress) * pct))
          if (pct >= 1) break
          await frame()
        }

        if (cancelled) return
        setCompletedPhases((prev) => [...prev, i])
      }

      // Minimum pacing done — stretch until the request settles, creeping the
      // bar so the wait reads as continued work rather than a stall.
      while (!cancelled && !settled) {
        setProgress((prev) => (prev < CREEP_CEILING ? prev + 0.2 : prev))
        await frame()
      }
      if (cancelled) return

      if (failed) {
        onErrorRef.current(error)
        return
      }

      setProgress(100)
      setFieldCount(result?.extractedFieldCount ?? 0)
      setIsDone(true)

      // Brief hold before calling onComplete
      await new Promise<void>((resolve) => setTimeout(resolve, 700))
      if (!cancelled && result) {
        onCompleteRef.current(result)
      }
    }

    runPhases()
    return () => { cancelled = true }
  }, [task])

  // Truncate filename for display
  const truncatedName = filename.length > 40
    ? filename.slice(0, 37) + '...'
    : filename

  return (
    <motion.div
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
      animate={reducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
      transition={reducedMotion ? { duration: 0.2 } : { duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      style={{
        background: isDone
          ? 'var(--color-surface-container-lowest)'
          : 'var(--color-surface-glass)',
        backdropFilter: isDone ? 'none' : 'var(--glass-standard-blur)',
        WebkitBackdropFilter: isDone ? 'none' : 'var(--glass-standard-blur)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: isDone ? 'var(--shadow-ambient)' : 'var(--shadow-subtle)',
        border: '1px solid var(--color-outline-subtle)',
        minHeight: 'var(--processing-card-min-height)',
        padding: 'var(--spacing-10)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--spacing-6)',
        transition: reducedMotion
          ? 'background 0.2s, backdrop-filter 0.2s, box-shadow 0.2s'
          : 'background 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      }}
    >
      {/* Filename chip */}
      <div
        style={{
          background: 'var(--color-action-primary-hover)',
          borderRadius: 'var(--radius-full)',
          padding: '4px 14px',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-label-sm)',
          fontWeight: 'var(--weight-medium)',
          color: 'var(--color-action-primary)',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={filename}
      >
        {truncatedName}
      </div>

      {/* Phase indicator dots */}
      <PhaseIndicator currentPhase={currentPhase} completedPhases={completedPhases} />

      {/* Status message or success */}
      {isDone ? (
        <motion.div
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={
            reducedMotion
              ? { duration: 0.2 }
              : { duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }
          }
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'var(--color-success-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 32 32"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 16l6 6 12-12"
                stroke="var(--color-success-text)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-label-lg)',
              fontWeight: 'var(--weight-medium)',
              color: 'var(--color-action-primary)',
              textAlign: 'center',
            }}
          >
            {fieldCount > 0
              ? `${fieldCount} field${fieldCount === 1 ? '' : 's'} filled in automatically`
              : 'Document processed — review the fields below'}
          </p>
        </motion.div>
      ) : (
        <PhaseMessages currentPhase={currentPhase} />
      )}

      {/* Progress track */}
      <div style={{ width: '100%' }}>
        <ProgressTrack progress={progress} />
      </div>
    </motion.div>
  )
}
